import { useState } from "react";
import { Check, Copy } from "lucide-react";

/**
 * "Ten lines is the product." A real IDE-styled code block on the left, side
 * annotations on the right — aligned to the line they describe. No hover
 * gimmicks: annotations are always visible, the reader can scan top-to-bottom
 * and grok the integration in <30 seconds.
 */

type Line = {
  n: number;
  // segments to color-render the source code
  parts: Array<{ t: string; c?: "kw" | "str" | "cm" | "fn" | "default" }>;
  /** Optional annotation rendered in the right column, aligned to this line. */
  note?: string;
};

const LINES: Line[] = [
  {
    n: 1,
    parts: [
      { t: "import", c: "kw" },
      { t: " { Noeracle } " },
      { t: "from", c: "kw" },
      { t: " " },
      { t: '"@noeracle/sdk"', c: "str" },
      { t: ";" },
    ],
    note: "The single SDK import. Published as @noeracle/sdk on npm.",
  },
  {
    n: 2,
    parts: [
      { t: "import", c: "kw" },
      { t: " { TransactionBuilder } " },
      { t: "from", c: "kw" },
      { t: " " },
      { t: '"@stellar/stellar-sdk"', c: "str" },
      { t: ";" },
    ],
  },
  { n: 3, parts: [{ t: "" }] },
  {
    n: 4,
    parts: [
      { t: "const", c: "kw" },
      { t: " oracle = " },
      { t: "new", c: "kw" },
      { t: " " },
      { t: "Noeracle", c: "fn" },
      { t: "({ network: " },
      { t: '"testnet"', c: "str" },
      { t: " });" },
    ],
    note: "Defaults to api.noeracle.org · testnet. Pass attestationUrl to override.",
  },
  {
    n: 5,
    parts: [
      { t: "const", c: "kw" },
      { t: " fresh  = " },
      { t: "await", c: "kw" },
      { t: " oracle." },
      { t: "fetchLatest", c: "fn" },
      { t: "([" },
      { t: '"BTC/USD"', c: "str" },
      { t: "]);" },
    ],
    note: "One HTTPS round-trip. Returns the latest signed message + Ed25519 signature.",
  },
  { n: 6, parts: [{ t: "" }] },
  {
    n: 7,
    parts: [
      { t: "// Prepend the verification op, then your application op(s).", c: "cm" },
    ],
  },
  {
    n: 8,
    parts: [
      { t: "const", c: "kw" },
      { t: " tx = " },
      { t: "new", c: "kw" },
      { t: " " },
      { t: "TransactionBuilder", c: "fn" },
      { t: "(account, opts)" },
    ],
  },
  {
    n: 9,
    parts: [
      { t: "  ." },
      { t: "addOperation", c: "fn" },
      { t: "(fresh." },
      { t: "toUpdateOp", c: "fn" },
      { t: "(NOERACLE_CONTRACT_ID))" },
    ],
    note: "Noeracle's verification op. Soroban checks the signature, stores the price.",
  },
  {
    n: 10,
    parts: [
      { t: "  ." },
      { t: "addOperation", c: "fn" },
      { t: "(perp." },
      { t: "call", c: "fn" },
      { t: '("open_position", /* ... */))' },
    ],
    note: "Your application op — runs against the just-verified price via get_price.",
  },
  {
    n: 11,
    parts: [
      { t: "  ." },
      { t: "setTimeout", c: "fn" },
      { t: "(" },
      { t: "30", c: "str" },
      { t: ")" },
    ],
  },
  {
    n: 12,
    parts: [{ t: "  ." }, { t: "build", c: "fn" }, { t: "();" }],
    note: "One transaction. Two intentions. Sub-500 ms freshness.",
  },
];

function classFor(c?: Line["parts"][number]["c"]): string {
  switch (c) {
    case "kw":
      return "text-m55";
    case "str":
      return "text-amber";
    case "cm":
      return "text-m45";
    case "fn":
      return "text-emerald";
    default:
      return "text-text";
  }
}

export function IntegrationBlock() {
  const [copied, setCopied] = useState(false);

  async function copyAll() {
    const text = LINES.map((l) =>
      l.parts.map((p) => p.t).join("")
    ).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* noop */
    }
  }

  return (
    <section
      className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-24 pb-12 sm:px-10 sm:pt-32"
      aria-labelledby="integrate-title"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-m45">
        <span className="text-emerald">●</span> INTEGRATE IN 10 LINES
      </p>
      <h2
        id="integrate-title"
        className="mt-6 font-serif text-3xl leading-[1.05] tracking-[-0.012em] text-text sm:text-5xl md:text-6xl"
      >
        <em className="italic text-emerald">Ten lines</em> is the product.
      </h2>
      <p className="mt-5 max-w-2xl text-base leading-relaxed text-m70">
        Drop the SDK in, prepend the verification op to your own transaction,
        execute against the just-verified price. No keeper to run, no wallet
        for the publisher, no extra round trip.
      </p>

      <div className="mt-10 overflow-hidden rounded-xl border border-border bg-surface/40">
        {/* IDE-style chrome */}
        <div className="flex items-center justify-between gap-3 border-b border-border bg-bg-deep/60 px-4 py-2.5">
          <div className="flex items-center gap-2.5">
            <div className="flex gap-1.5">
              <span className="size-2.5 rounded-full bg-m15" />
              <span className="size-2.5 rounded-full bg-m15" />
              <span className="size-2.5 rounded-full bg-m15" />
            </div>
            <span className="ml-3 inline-flex items-center gap-2 rounded-t-sm bg-surface/60 px-3 py-1 font-mono text-[10.5px] uppercase tracking-[0.18em] text-m70">
              <span className="text-emerald">●</span> open-position.ts
            </span>
          </div>
          <button
            type="button"
            onClick={copyAll}
            className="inline-flex items-center gap-1.5 rounded border border-border bg-surface/40 px-2.5 py-1 font-mono text-[9.5px] uppercase tracking-[0.22em] text-m55 transition-colors hover:border-emerald/40 hover:text-emerald"
          >
            {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>

        {/* Two-column body: code + annotations */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px]">
          <div className="overflow-x-auto bg-bg-deep/40">
            <pre className="m-0 px-6 py-6 font-mono text-[13px] leading-[1.85]">
              <code>
                {LINES.map((line) => (
                  <div key={line.n} className="flex">
                    <span className="select-none w-8 shrink-0 pr-4 text-right text-m30">
                      {line.parts.length === 1 && line.parts[0].t === "" ? "" : line.n}
                    </span>
                    <span className="flex-1 whitespace-pre">
                      {line.parts.map((p, i) => (
                        <span key={i} className={classFor(p.c)}>
                          {p.t}
                        </span>
                      ))}
                    </span>
                  </div>
                ))}
              </code>
            </pre>
          </div>

          <div className="hidden lg:block border-l border-border bg-bg-deep/20">
            <div className="py-6">
              {LINES.map((line) => (
                <div
                  key={line.n}
                  className="flex items-start gap-2 px-5 text-[11.5px] leading-[1.85] min-h-[1.85em]"
                >
                  {line.note ? (
                    <>
                      <span className="text-m30 select-none mt-[3px]">↪</span>
                      <span className="text-m55">{line.note}</span>
                    </>
                  ) : (
                    <span className="opacity-0 select-none">·</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile-friendly annotations row */}
        <div className="lg:hidden border-t border-border bg-bg-deep/20 px-6 py-5 space-y-2">
          {LINES.filter((l) => l.note).map((l) => (
            <div key={l.n} className="text-[12px] leading-relaxed">
              <span className="font-mono text-emerald mr-2">L{l.n}</span>
              <span className="text-m70">{l.note}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
